import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/data-table";
import { ExportFormatMenu } from "@/features/content/components/ExportFormatMenu";
import { ContentReportPanel } from "@/features/reports/components/ContentReportPanel";
import { ReportMetricCard } from "@/features/reports/components/ReportMetricCard";
import { ReportSectionError } from "@/features/reports/components/ReportSectionError";
import { ReportTableSkeleton } from "@/features/reports/components/ReportTableSkeleton";
import { WebsiteAnalyticsPanel } from "@/features/reports/components/WebsiteAnalyticsPanel";
import { useScopedDepartments } from "@/features/reports/hooks/useReportScope";
import {
  isReportSectionLoading,
  normalizeReportDateFilters,
} from "@/features/reports/lib/report-query-utils";
import { reportService } from "@/features/reports/services/reportService";
import { auditLogService } from "@/features/audit-logs/services/auditLogService";
import { useDepartments } from "@/features/departments/hooks/useDepartmentsQueries";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import { handleApiError } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type { AuditLog } from "@/types/audit-log";
import type { ContentExportFormat } from "@/types/content";
import type { User } from "@/types/user";

type Tab = "content" | "activity" | "website";
const PAGE_SIZE = 25;

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "مدير عام",
  SUB_ADMIN: "مدير فرعي",
  EDITOR: "محرر",
};

const loginColumns: ColumnDef<User>[] = [
  { accessorKey: "email", header: "البريد" },
  {
    id: "name",
    header: "الاسم",
    accessorFn: (u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || "—",
  },
  {
    id: "role",
    header: "الدور",
    accessorFn: (u) => getRoleDisplayName(u.role),
  },
  { accessorKey: "departmentId", header: "الدائرة" },
  {
    id: "lastLoginAt",
    header: "آخر دخول",
    accessorFn: (u) => formatOptionalArabicDateTime(u.lastLoginAt),
  },
];

const auditColumns: ColumnDef<AuditLog>[] = [
  { accessorKey: "action", header: "الإجراء" },
  { accessorKey: "resource", header: "المورد" },
  { accessorKey: "userId", header: "المستخدم" },
  { accessorKey: "ipAddress", header: "IP" },
  {
    id: "createdAt",
    header: "التاريخ",
    accessorFn: (row) => formatOptionalArabicDateTime(row.createdAt),
  },
];

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab: Tab =
    rawTab === "activity" || rawTab === "website" ? rawTab : "content";

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const department = searchParams.get("department") ?? "all";

  const [loginsPage, setLoginsPage] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const [contentAuditPage, setContentAuditPage] = useState(0);
  const [isExportingUsers, setIsExportingUsers] = useState(false);
  const [isExportingLogins, setIsExportingLogins] = useState(false);
  const [isExportingAudit, setIsExportingAudit] = useState(false);
  const [isExportingContentAudit, setIsExportingContentAudit] = useState(false);

  const patchParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  };

  const setTab = (t: Tab) => patchParams({ tab: t === "content" ? undefined : t });

  const { data: deptData } = useDepartments({ limit: 100 });
  const allDepartments = useMemo(() => deptData?.items ?? [], [deptData]);
  const departments = useScopedDepartments(allDepartments);

  const reportWindow = useMemo(
    () => normalizeReportDateFilters(from, to),
    [from, to],
  );

  const departmentFilter =
    department === "all" ? undefined : department;

  // --- User Activity tab ---
  const usersReport = useQuery({
    queryKey: queryKeys.reports.users(reportWindow),
    queryFn: () => reportService.usersReport(reportWindow),
    enabled: tab === "activity",
  });

  const exportUsers = async (format: ContentExportFormat) => {
    setIsExportingUsers(true);
    try {
      await reportService.exportUsersReport({ ...reportWindow, format });
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير تقرير المستخدمين" });
    } finally {
      setIsExportingUsers(false);
    }
  };

  const loginsQuery = useMemo(
    () => ({
      departmentId: departmentFilter,
      page: loginsPage + 1,
      pageSize: PAGE_SIZE,
    }),
    [departmentFilter, loginsPage],
  );
  const logins = useQuery({
    queryKey: queryKeys.reports.userActivityLogins(loginsQuery),
    queryFn: () => reportService.userActivityLogins(loginsQuery),
    enabled: tab === "activity",
  });

  const auditQuery = useMemo(
    () => ({
      ...reportWindow,
      departmentId: departmentFilter,
      page: auditPage + 1,
      pageSize: PAGE_SIZE,
    }),
    [reportWindow, departmentFilter, auditPage],
  );
  const audit = useQuery({
    queryKey: queryKeys.reports.userActivityAudit(auditQuery),
    queryFn: () => reportService.userActivityAudit(auditQuery),
    enabled: tab === "activity",
  });

  const contentAuditQuery = useMemo(
    () => ({
      ...reportWindow,
      departmentId: departmentFilter,
      contentOnly: true,
      page: contentAuditPage + 1,
      pageSize: PAGE_SIZE,
    }),
    [reportWindow, departmentFilter, contentAuditPage],
  );
  const contentAudit = useQuery({
    queryKey: queryKeys.reports.userActivityAudit({
      ...contentAuditQuery,
      scope: "content",
    }),
    queryFn: () => reportService.userActivityAudit(contentAuditQuery),
    enabled: tab === "activity",
  });

  const usersReportLoading = isReportSectionLoading(
    usersReport.isPending,
    usersReport.isFetching,
    usersReport.data,
  );
  const loginsLoading = isReportSectionLoading(
    logins.isPending,
    logins.isFetching,
    logins.data,
  );
  const auditLoading = isReportSectionLoading(
    audit.isPending,
    audit.isFetching,
    audit.data,
  );
  const contentAuditLoading = isReportSectionLoading(
    contentAudit.isPending,
    contentAudit.isFetching,
    contentAudit.data,
  );

  const deptNameFor = (id?: string) => {
    if (!id) return "—";
    const d = allDepartments.find((d) => d.id === id);
    return d?.nameAr ?? d?.name ?? id;
  };
  const loginRows = (logins.data?.items ?? []).map((u) => ({
    ...u,
    departmentId: deptNameFor(u.departmentId ?? undefined),
  }));

  const exportLogins = async (format: ContentExportFormat) => {
    setIsExportingLogins(true);
    try {
      await reportService.exportUserActivityLogins({ ...loginsQuery, format });
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير سجل تسجيلات الدخول" });
    } finally {
      setIsExportingLogins(false);
    }
  };

  const exportAudit = async () => {
    setIsExportingAudit(true);
    try {
      await auditLogService.exportCsv(auditQuery);
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير سجل الإجراءات" });
    } finally {
      setIsExportingAudit(false);
    }
  };

  const exportContentAudit = async () => {
    setIsExportingContentAudit(true);
    try {
      await auditLogService.exportCsv({
        ...contentAuditQuery,
        contentOnly: true,
      });
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير إجراءات المحتوى" });
    } finally {
      setIsExportingContentAudit(false);
    }
  };

  const departmentFilterControl = (
    <Select
      value={department}
      onValueChange={(v) => patchParams({ department: v === "all" ? undefined : v })}
    >
      <SelectTrigger className="w-44">
        <SelectValue placeholder="الدائرة" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">كل الدوائر</SelectItem>
        {departments.map((d) => (
          <SelectItem key={d.id} value={d.id}>
            {d.nameAr ?? d.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const dateRangeControls = (
    <>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        من تاريخ
        <input
          type="date"
          value={from}
          onChange={(e) => patchParams({ from: e.target.value || undefined })}
          className="rounded-md border border-border px-2 py-1 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        إلى تاريخ
        <input
          type="date"
          value={to}
          onChange={(e) => patchParams({ to: e.target.value || undefined })}
          className="rounded-md border border-border px-2 py-1 text-sm"
        />
      </label>
    </>
  );

  return (
    <AdminPageShell
      title="التقارير والتحليلات"
      description="تقارير المحتوى ونشاط المستخدمين والطلبات وتحليلات الموقع الإلكتروني"
    >
      <TabsList className="mb-4">
        <TabsTrigger
          data-state={tab === "content" ? "active" : "inactive"}
          onClick={() => setTab("content")}
        >
          المحتوى
        </TabsTrigger>
        <TabsTrigger
          data-state={tab === "activity" ? "active" : "inactive"}
          onClick={() => setTab("activity")}
        >
          نشاط المستخدمين
        </TabsTrigger>
        <TabsTrigger
          data-state={tab === "website" ? "active" : "inactive"}
          onClick={() => setTab("website")}
        >
          تحليلات الموقع الإلكتروني
        </TabsTrigger>
      </TabsList>

      {tab === "content" && (
        <ContentReportPanel
          from={from}
          to={to}
          onChangeFrom={(v) => patchParams({ from: v })}
          onChangeTo={(v) => patchParams({ to: v })}
          onClear={() => patchParams({ from: undefined, to: undefined })}
        />
      )}

      {tab === "activity" && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end gap-3">
            {dateRangeControls}
            {departmentFilterControl}
            <ExportFormatMenu
              isExporting={isExportingUsers}
              onExport={exportUsers}
              label="تصدير المستخدمين"
            />
          </div>

          {usersReport.error ? (
            <ReportSectionError onRetry={() => usersReport.refetch()} />
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <ReportMetricCard
                  title="إجمالي المستخدمين"
                  value={usersReport.data?.total ?? "—"}
                  isLoading={usersReportLoading}
                />
                <ReportMetricCard
                  title="نشطون"
                  value={usersReport.data?.active ?? "—"}
                  valueClassName="text-primary"
                  isLoading={usersReportLoading}
                />
                <ReportMetricCard
                  title="غير نشطين"
                  value={usersReport.data?.inactive ?? "—"}
                  valueClassName="text-muted-foreground"
                  isLoading={usersReportLoading}
                />
                <ReportMetricCard
                  title="جدد ضمن الفترة"
                  value={usersReport.data?.newInRange ?? "—"}
                  isLoading={usersReportLoading}
                />
              </div>

              <Card className="overflow-hidden text-sm">
                <div className="border-b border-border px-3 py-2 text-right text-xs font-medium text-foreground">
                  المستخدمون حسب الدور
                </div>
                <table className="min-w-full divide-y divide-border text-xs">
                  <thead className="bg-muted/30 text-[11px] text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-right">الدور</th>
                      <th className="px-3 py-2 text-center">العدد</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {usersReportLoading ? (
                      <tr>
                        <td colSpan={2} className="px-3 py-4 text-center text-muted-foreground">
                          جاري التحميل...
                        </td>
                      </tr>
                    ) : (
                      Object.entries(usersReport.data?.byRole ?? {}).map(([role, count]) => (
                        <tr key={role} className="hover:bg-muted/20">
                          <td className="px-3 py-1.5 text-right">
                            {ROLE_LABELS[role] ?? role}
                          </td>
                          <td className="px-3 py-1.5 text-center">{count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Card>
            </>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">
                آخر تسجيلات الدخول
              </h3>
              <ExportFormatMenu
                isExporting={isExportingLogins}
                onExport={exportLogins}
              />
            </div>
            {logins.error ? (
              <ReportSectionError onRetry={() => logins.refetch()} />
            ) : loginsLoading ? (
              <ReportTableSkeleton columns={5} />
            ) : (
              <DataTable
                columns={loginColumns}
                data={loginRows}
                manualPagination={{
                  pageIndex: loginsPage,
                  pageCount: logins.data?.pagination.pageCount ?? 1,
                  onPageChange: setLoginsPage,
                }}
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">
                إجراءات المحتوى
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={exportContentAudit}
                disabled={isExportingContentAudit}
              >
                تصدير CSV
              </Button>
            </div>
            {contentAudit.error ? (
              <ReportSectionError onRetry={() => contentAudit.refetch()} />
            ) : contentAuditLoading ? (
              <ReportTableSkeleton columns={5} />
            ) : (
              <DataTable
                columns={auditColumns}
                data={contentAudit.data?.items ?? []}
                manualPagination={{
                  pageIndex: contentAuditPage,
                  pageCount: contentAudit.data?.pagination.pageCount ?? 1,
                  onPageChange: setContentAuditPage,
                }}
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-foreground">سجل الإجراءات</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={exportAudit}
                disabled={isExportingAudit}
              >
                تصدير CSV
              </Button>
            </div>
            {audit.error ? (
              <ReportSectionError onRetry={() => audit.refetch()} />
            ) : auditLoading ? (
              <ReportTableSkeleton columns={5} />
            ) : (
              <DataTable
                columns={auditColumns}
                data={audit.data?.items ?? []}
                manualPagination={{
                  pageIndex: auditPage,
                  pageCount: audit.data?.pagination.pageCount ?? 1,
                  onPageChange: setAuditPage,
                }}
              />
            )}
          </div>
        </div>
      )}

      {tab === "website" && (
        <WebsiteAnalyticsPanel
          from={from}
          to={to}
          onChangeFrom={(v) => patchParams({ from: v })}
          onChangeTo={(v) => patchParams({ to: v })}
        />
      )}
    </AdminPageShell>
  );
}
